import React from 'react';

/*
 * Accepted-payment marks, one shared set.
 *
 * These are renditions of the schemes' own marks, shown to say "we take this" —
 * the standard use. They live here rather than in PaymentMethods.jsx because the
 * summary's trust strip needs the same tiles: two drifting copies of a Visa badge
 * is how a checkout ends up with two slightly different blues.
 *
 * Every tile is the same 34×22 box on the same baseline, so any subset of them
 * lines up in a row without per-brand nudging. White field + hairline for the
 * card schemes; brand field where the mark IS the field (Klarna's pink).
 */

export const BrandVisa = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Visa" role="img"><rect width="34" height="22" rx="3" fill="#fff" stroke="#e6e6ef" /><text x="17" y="15" textAnchor="middle" fontFamily="Arial, sans-serif" fontStyle="italic" fontWeight="700" fontSize="10" fill="#1A1F71">VISA</text></svg>
);

export const BrandMC = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Mastercard" role="img"><rect width="34" height="22" rx="3" fill="#fff" stroke="#e6e6ef" /><circle cx="14" cy="11" r="6" fill="#EB001B" /><circle cx="20" cy="11" r="6" fill="#F79E1B" /><path d="M17 6.2a6 6 0 0 1 0 9.6 6 6 0 0 1 0-9.6z" fill="#FF5F00" /></svg>
);

export const BrandMaestro = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Maestro" role="img"><rect width="34" height="22" rx="3" fill="#fff" stroke="#e6e6ef" /><circle cx="14" cy="11" r="6" fill="#0099DF" /><circle cx="20" cy="11" r="6" fill="#ED0006" /><path d="M17 6.2a6 6 0 0 1 0 9.6 6 6 0 0 1 0-9.6z" fill="#6C6BBD" /></svg>
);

export const BrandDiners = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Diners Club" role="img"><rect width="34" height="22" rx="3" fill="#fff" stroke="#e6e6ef" /><circle cx="17" cy="11" r="6.5" fill="#0079BE" /><circle cx="17" cy="11" r="3" fill="#fff" /></svg>
);

export const BrandKlarna = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Klarna" role="img">
    <rect width="34" height="22" rx="3" fill="#FFB3C7" />
    <text x="17" y="14.6" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="8" fill="#0B051D">Klarna</text>
  </svg>
);

// The Apple glyph on its own 24 grid, scaled into the tile beside the word.
export const BrandApplePay = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Apple Pay" role="img">
    <rect width="34" height="22" rx="3" fill="#fff" stroke="#e6e6ef" />
    <g transform="translate(4.6 4.4) scale(0.52)" fill="#000">
      <path d="M16.36 12.9c-.02-2.06 1.68-3.05 1.76-3.1-.96-1.4-2.45-1.6-2.98-1.62-1.27-.13-2.48.75-3.12.75-.64 0-1.64-.73-2.7-.71-1.39.02-2.67.81-3.38 2.05-1.44 2.5-.37 6.2 1.03 8.23.69.99 1.51 2.1 2.58 2.06 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.61.67 2.7.65 1.12-.02 1.83-1.01 2.51-2.01.79-1.15 1.12-2.27 1.14-2.33-.03-.01-2.18-.84-2.2-3.32zM14.3 6.84c.57-.69.95-1.65.85-2.6-.82.03-1.81.54-2.4 1.23-.53.61-.99 1.59-.87 2.52.91.07 1.85-.46 2.42-1.15z" />
    </g>
    <text x="23" y="14.6" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="600" fontSize="8" fill="#000">Pay</text>
  </svg>
);

export const BrandGooglePay = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" aria-label="Google Pay" role="img">
    <rect width="34" height="22" rx="3" fill="#fff" stroke="#e6e6ef" />
    <g transform="translate(4.4 4.6) scale(0.53)">
      <path fill="#4285F4" d="M21.6 12.23c0-.64-.06-1.25-.16-1.84H12v3.48h5.38a4.6 4.6 0 0 1-1.99 3.02v2.5h3.22c1.89-1.74 2.99-4.31 2.99-7.16z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.61-2.43l-3.22-2.5c-.9.6-2.05.96-3.39.96-2.6 0-4.8-1.76-5.59-4.13H3.09v2.6A10 10 0 0 0 12 22z" />
      <path fill="#FBBC04" d="M6.41 13.9a6 6 0 0 1 0-3.8V7.5H3.09a10 10 0 0 0 0 9l3.32-2.6z" />
      <path fill="#EA4335" d="M12 5.98c1.47 0 2.79.5 3.83 1.5l2.86-2.87C16.95 2.99 14.69 2 12 2A10 10 0 0 0 3.09 7.5l3.32 2.6C7.2 7.74 9.4 5.98 12 5.98z" />
    </g>
    <text x="23" y="14.6" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="600" fontSize="8" fill="#3C4043">Pay</text>
  </svg>
);

/* The card schemes Viva settles, as one strip — used on the card payment row. */
export const CardBrands = () => (
  <span className="cn-method__brands">
    <BrandVisa /><BrandMC /><BrandMaestro /><BrandDiners />
  </span>
);
